import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import GuestOverlay from "@/components/auth/GuestOverlay";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { toast as sonnerToast } from "sonner";
import {
  Stamp,
  Loader2,
  Download,
  CheckCircle2,
  AlertTriangle,
  Link as LinkIcon,
  Type,
  Move,
  Eye,
  RefreshCw,
  Copy,
  Clock,
  History,
  ExternalLink,
  Image as ImageIcon,
  Upload,
  X,
  Video,
  Plus,
  Pencil,
  Trash2
} from "lucide-react";

// Permanent watermark server URL
const WATERMARK_API_URL = "https://wm.gencnx.com";
const HISTORY_KEY = "watermark_history_v2";
const CONFIG_KEY = "watermark_config_v1";
const CHANNELS_KEY = "watermark_channels_v1";
const ACTIVE_CHANNEL_KEY = "watermark_active_channel_v1";
const EXPIRY_HOURS = 72;
const WATERMARK_CREDITS = 5;

interface WatermarkConfig {
  videoUrl: string;
  fullhd: boolean;
  videoSpeed: number;
  // Text watermark
  enableText: boolean;
  text: string;
  textSize: number;
  textLocation: string;
  textColor: string;
  textOpacity: number;
  textMargin: number;
  // Image watermark
  enableImage: boolean;
  imageUrl: string;
  imageSize: number;
  imageLocation: string;
  imageOpacity: number;
  imageMargin: number;
}

interface JobResult {
  status: "idle" | "queued" | "processing" | "done" | "failed" | "expired" | "error";
  jobId?: string;
  resultUrl?: string;
  message?: string;
  processingTime?: number;
}

interface HistoryItem {
  jobId: string;
  sourceUrl: string;
  text?: string;
  hasImage?: boolean;
  resultUrl: string;
  createdAt: number;
  expiresAt: number;
}

// Saved config (without videoUrl which changes per video)
interface SavedWatermarkConfig {
  fullhd: boolean;
  videoSpeed: number;
  enableText: boolean;
  text: string;
  textSize: number;
  textLocation: string;
  textColor: string;
  textOpacity: number;
  textMargin: number;
  enableImage: boolean;
  imageUrl: string;
  imageSize: number;
  imageLocation: string;
  imageOpacity: number;
  imageMargin: number;
}

interface WatermarkChannel {
  id: string;
  name: string;
  config: SavedWatermarkConfig;
  createdAt: number;
  updatedAt: number;
}

type ChannelDialogMode = "create" | "edit";

const createDefaultConfig = (): WatermarkConfig => ({
  videoUrl: "",
  fullhd: true,
  videoSpeed: 1,
  enableText: true,
  text: "",
  textSize: 32,
  textLocation: "bottom-center",
  textColor: "#FFFFFF",
  textOpacity: 70,
  textMargin: 8,
  enableImage: false,
  imageUrl: "",
  imageSize: 128,
  imageLocation: "top-left",
  imageOpacity: 80,
  imageMargin: 8
});

const toSavedConfig = (config: WatermarkConfig): SavedWatermarkConfig => ({
  fullhd: config.fullhd,
  videoSpeed: config.videoSpeed,
  enableText: config.enableText,
  text: config.text,
  textSize: config.textSize,
  textLocation: config.textLocation,
  textColor: config.textColor,
  textOpacity: config.textOpacity,
  textMargin: config.textMargin,
  enableImage: config.enableImage,
  imageUrl: config.imageUrl,
  imageSize: config.imageSize,
  imageLocation: config.imageLocation,
  imageOpacity: config.imageOpacity,
  imageMargin: config.imageMargin
});

const loadLegacyConfig = (): SavedWatermarkConfig | null => {
  try {
    const saved = localStorage.getItem(CONFIG_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch (e) {
    console.error("Error loading saved config:", e);
    return null;
  }
};

const createInitialChannels = (): WatermarkChannel[] => {
  const now = Date.now();
  const legacyConfig = loadLegacyConfig();

  try {
    const saved = localStorage.getItem(CHANNELS_KEY);
    if (saved) {
      const channels: WatermarkChannel[] = JSON.parse(saved);
      if (Array.isArray(channels) && channels.length > 0) {
        return channels;
      }
    }
  } catch (e) {
    console.error("Error loading watermark channels:", e);
  }

  return [{
    id: `channel_${now}`,
    name: legacyConfig?.text?.trim() || "Default Channel",
    config: toSavedConfig({ ...createDefaultConfig(), ...legacyConfig }),
    createdAt: now,
    updatedAt: now
  }];
};

// 3x3 grid positions + center variations
const locationGrid = [
  [
    { id: "top-left", labelTh: "↖", labelEn: "↖" },
    { id: "top-center", labelTh: "↑", labelEn: "↑" },
    { id: "top-right", labelTh: "↗", labelEn: "↗" }
  ],
  [
    { id: "center-left", labelTh: "←", labelEn: "←" },
    { id: "center", labelTh: "●", labelEn: "●" },
    { id: "center-right", labelTh: "→", labelEn: "→" }
  ],
  [
    { id: "bottom-left", labelTh: "↙", labelEn: "↙" },
    { id: "bottom-center", labelTh: "↓", labelEn: "↓" },
    { id: "bottom-right", labelTh: "↘", labelEn: "↘" }
  ]
];

// Map position to CSS for preview overlay
const getPositionStyle = (position: string, margin: number = 8): React.CSSProperties => {
  const m = `${margin}%`;
  const styles: Record<string, React.CSSProperties> = {
    "top-left": { top: m, left: m, transform: "none" },
    "top-center": { top: m, left: "50%", transform: "translateX(-50%)" },
    "top-right": { top: m, right: m, transform: "none" },
    "center-left": { top: "50%", left: m, transform: "translateY(-50%)" },
    "center": { top: "50%", left: "50%", transform: "translate(-50%, -50%)" },
    "center-right": { top: "50%", right: m, transform: "translateY(-50%)" },
    "bottom-left": { bottom: m, left: m, transform: "none" },
    "bottom-center": { bottom: m, left: "50%", transform: "translateX(-50%)" },
    "bottom-right": { bottom: m, right: m, transform: "none" },
    "bouncing": { top: "50%", left: "50%", transform: "translate(-50%, -50%)", animation: "bounce 2s infinite" }
  };
  return styles[position] || styles["bottom-center"];
};

const colorOptions = [
  { id: "white", labelTh: "ขาว", labelEn: "White", value: "#FFFFFF" },
  { id: "black", labelTh: "ดำ", labelEn: "Black", value: "#000000" },
  { id: "red", labelTh: "แดง", labelEn: "Red", value: "#FF0000" },
  { id: "yellow", labelTh: "เหลือง", labelEn: "Yellow", value: "#FFFF00" },
  { id: "blue", labelTh: "น้ำเงิน", labelEn: "Blue", value: "#0066FF" },
  { id: "green", labelTh: "เขียว", labelEn: "Green", value: "#00FF00" }
];

// Speed presets for quick selection
const speedPresets = [
  { value: 0.92, label: "0.92x" },
  { value: 0.95, label: "0.95x" },
  { value: 1, label: "1x" },
  { value: 1.05, label: "1.05x" }
];

const Watermark = () => {
  const { t } = useLanguage();
  const { user, profile, refreshProfile, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const location = useLocation();
  const imageInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);

  // Get videoUrl from navigation state (from History page)
  const incomingVideoUrl = (location.state as { videoUrl?: string })?.videoUrl;

  const [channels, setChannels] = useState<WatermarkChannel[]>(() => createInitialChannels());
  const [activeChannelId, setActiveChannelId] = useState(() => {
    try {
      return localStorage.getItem(ACTIVE_CHANNEL_KEY) || "";
    } catch {
      return "";
    }
  });
  const [channelDialogOpen, setChannelDialogOpen] = useState(false);
  const [channelDialogMode, setChannelDialogMode] = useState<ChannelDialogMode>("create");
  const [channelNameInput, setChannelNameInput] = useState("");
  const [channelTextInput, setChannelTextInput] = useState("");

  // Deduct credits and save to generation_logs
  const saveWatermarkToHistory = async (resultUrl: string, prompt: string) => {
    if (!user) return;

    try {
      // Check credits first
      if (!profile || profile.credits < WATERMARK_CREDITS) {
        sonnerToast.error(t(
          `เครดิตไม่เพียงพอ ต้องการ ${WATERMARK_CREDITS} เครดิต`,
          `Insufficient credits. Need ${WATERMARK_CREDITS} credits`
        ));
        return;
      }

      // Deduct credits using RPC
      const { data, error } = await supabase.rpc('deduct_credits_for_generation' as any, {
        p_user_id: user.id,
        p_credits_required: WATERMARK_CREDITS,
        p_service_type: 'watermark',
        p_model: 'Watermark',
        p_prompt: prompt
      });

      if (error) {
        console.error("Error deducting credits:", error);
        sonnerToast.error(t("เกิดข้อผิดพลาดในการหักเครดิต", "Error deducting credits"));
        return;
      }

      const result = data as { success: boolean; log_id?: string; error?: string };

      if (!result.success) {
        sonnerToast.error(t(
          result.error === "Insufficient credits" ? "เครดิตไม่เพียงพอ" : "เกิดข้อผิดพลาด",
          result.error || "Error"
        ));
        return;
      }

      // Update log with result URL and completed status
      if (result.log_id) {
        await supabase
          .from("generation_logs")
          .update({
            result_url: resultUrl,
            status: "completed",
            completed_at: new Date().toISOString()
          })
          .eq("id", result.log_id);
      }

      // Refresh profile to show updated credits
      await refreshProfile();

      sonnerToast.success(t(`หักเครดิต ${WATERMARK_CREDITS} เครดิต`, `Deducted ${WATERMARK_CREDITS} credits`));
    } catch (err) {
      console.error("Error saving watermark to history:", err);
    }
  };

  const [config, setConfig] = useState<WatermarkConfig>(() => {
    const initialChannels = createInitialChannels();
    const savedActiveId = localStorage.getItem(ACTIVE_CHANNEL_KEY);
    const activeChannel = initialChannels.find(channel => channel.id === savedActiveId) || initialChannels[0];
    return { ...createDefaultConfig(), ...activeChannel?.config };
  });

  const [isProcessing, setIsProcessing] = useState(false);
  const [jobResult, setJobResult] = useState<JobResult>({ status: "idle" });
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [historyItems, setHistoryItems] = useState<HistoryItem[]>([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [videoUploadProgress, setVideoUploadProgress] = useState(0);

  // Video History Picker state
  const [showVideoHistory, setShowVideoHistory] = useState(false);
  const [videoHistoryItems, setVideoHistoryItems] = useState<Array<{
    id: string;
    service_type: string;
    model: string;
    prompt: string;
    result_url: string;
    created_at: string;
  }>>([]);
  const [loadingVideoHistory, setLoadingVideoHistory] = useState(false);

  useEffect(() => {
    loadHistory();
  }, []);

  useEffect(() => {
    if (channels.length === 0) return;

    const validActive = channels.some(channel => channel.id === activeChannelId);
    if (!validActive) {
      const nextActiveId = channels[0].id;
      setActiveChannelId(nextActiveId);
      setConfig(prev => ({ ...prev, ...channels[0].config }));
    }
  }, [activeChannelId, channels]);

  // Set video URL from navigation state (from History page)
  useEffect(() => {
    if (incomingVideoUrl) {
      setConfig(prev => ({ ...prev, videoUrl: incomingVideoUrl }));
      // Clear the state to prevent re-setting on re-renders
      window.history.replaceState({}, document.title);
    }
  }, [incomingVideoUrl]);

  // Save config to the active channel when it changes (excluding videoUrl)
  useEffect(() => {
    if (!activeChannelId) return;

    const savedConfig = toSavedConfig(config);
    localStorage.setItem(CONFIG_KEY, JSON.stringify(savedConfig));
    localStorage.setItem(ACTIVE_CHANNEL_KEY, activeChannelId);

    setChannels(prev => {
      const updated = prev.map(channel => (
        channel.id === activeChannelId
          ? { ...channel, config: savedConfig, updatedAt: Date.now() }
          : channel
      ));
      localStorage.setItem(CHANNELS_KEY, JSON.stringify(updated));
      return updated;
    });
  }, [activeChannelId, config]);

  const activeChannel = channels.find(channel => channel.id === activeChannelId) || channels[0];

  const handleSelectChannel = (channelId: string) => {
    const channel = channels.find(item => item.id === channelId);
    if (!channel) return;

    setActiveChannelId(channelId);
    setConfig(prev => ({
      ...createDefaultConfig(),
      ...channel.config,
      videoUrl: prev.videoUrl
    }));
  };

  const openCreateChannelDialog = () => {
    setChannelDialogMode("create");
    setChannelNameInput("");
    setChannelTextInput("");
    setChannelDialogOpen(true);
  };

  const openEditChannelDialog = () => {
    if (!activeChannel) return;
    setChannelDialogMode("edit");
    setChannelNameInput(activeChannel.name);
    setChannelTextInput(config.text);
    setChannelDialogOpen(true);
  };

  const handleSaveChannel = () => {
    const channelName = channelNameInput.trim();
    const watermarkText = channelTextInput.trim();

    if (!channelName) {
      toast({
        variant: "destructive",
        title: t("กรุณากรอกชื่อช่อง", "Please enter channel name")
      });
      return;
    }

    if (channelDialogMode === "create") {
      const now = Date.now();
      const nextConfig = {
        ...toSavedConfig(createDefaultConfig()),
        text: watermarkText || channelName
      };
      const newChannel: WatermarkChannel = {
        id: `channel_${now}_${Math.random().toString(36).slice(2, 8)}`,
        name: channelName,
        config: nextConfig,
        createdAt: now,
        updatedAt: now
      };

      const updated = [...channels, newChannel];
      setChannels(updated);
      localStorage.setItem(CHANNELS_KEY, JSON.stringify(updated));
      setActiveChannelId(newChannel.id);
      setConfig(prev => ({
        ...createDefaultConfig(),
        ...nextConfig,
        videoUrl: prev.videoUrl
      }));
      toast({
        title: t("สร้างช่องแล้ว", "Channel created"),
        description: t("ค่าลายน้ำของช่องนี้จะถูกจำแยกไว้", "This channel will keep its own watermark settings")
      });
    } else if (activeChannel) {
      const nextConfig = {
        ...toSavedConfig(config),
        text: watermarkText || config.text
      };
      const updated = channels.map(channel => (
        channel.id === activeChannel.id
          ? { ...channel, name: channelName, config: nextConfig, updatedAt: Date.now() }
          : channel
      ));
      setChannels(updated);
      localStorage.setItem(CHANNELS_KEY, JSON.stringify(updated));
      setConfig(prev => ({ ...prev, text: nextConfig.text }));
      toast({
        title: t("แก้ไขช่องแล้ว", "Channel updated")
      });
    }

    setChannelDialogOpen(false);
  };

  const handleDeleteChannel = () => {
    if (!activeChannel || channels.length <= 1) {
      toast({
        variant: "destructive",
        title: t("ต้องมีอย่างน้อย 1 ช่อง", "At least 1 channel is required")
      });
      return;
    }

    const confirmed = window.confirm(t(
      `ลบช่อง "${activeChannel.name}"?`,
      `Delete "${activeChannel.name}" channel?`
    ));
    if (!confirmed) return;

    const updated = channels.filter(channel => channel.id !== activeChannel.id);
    const nextActive = updated[0];
    setChannels(updated);
    localStorage.setItem(CHANNELS_KEY, JSON.stringify(updated));
    setActiveChannelId(nextActive.id);
    setConfig(prev => ({
      ...createDefaultConfig(),
      ...nextActive.config,
      videoUrl: prev.videoUrl
    }));
    toast({
      title: t("ลบช่องแล้ว", "Channel deleted")
    });
  };

  const loadHistory = () => {
    try {
      const stored = localStorage.getItem(HISTORY_KEY);
      if (stored) {
        const items: HistoryItem[] = JSON.parse(stored);
        const now = Date.now();
        const validItems = items.filter(item => item.expiresAt > now);
        setHistoryItems(validItems);
        if (validItems.length !== items.length) {
          localStorage.setItem(HISTORY_KEY, JSON.stringify(validItems));
        }
      }
    } catch (e) {
      console.error("Error loading history:", e);
    }
  };

  // Fetch video history from database for selection
  const fetchVideoHistory = async () => {
    if (!user) return;

    setLoadingVideoHistory(true);
    try {
      const { data, error } = await supabase
        .from("generation_logs")
        .select("id, service_type, model, prompt, result_url, created_at")
        .eq("user_id", user.id)
        .in("service_type", ["video", "video_motion"] as any)
        .eq("status", "completed")
        .not("result_url", "is", null)
        .or("is_deleted.is.null,is_deleted.eq.false")
        .order("created_at", { ascending: false })
        .limit(50);

      if (error) throw error;

      // Filter to only include items with valid video URLs
      const validItems = (data || []).filter(item => {
        const url = getFirstVideoUrlFromResult(item.result_url);
        return url && url.startsWith('http');
      });

      setVideoHistoryItems(validItems as any[]);
    } catch (error: any) {
      console.error("Error fetching video history:", error);
      toast({
        variant: "destructive",
        title: t("ไม่สามารถโหลดประวัติวิดีโอได้", "Failed to load video history"),
        description: error.message
      });
    } finally {
      setLoadingVideoHistory(false);
    }
  };

  // Helper to get first video URL from result_url (which may be JSON or comma-separated)
  const getFirstVideoUrlFromResult = (resultUrl: string | null): string | null => {
    if (!resultUrl) return null;

    let videoUrl = resultUrl;
    if (videoUrl.startsWith('{')) {
      try {
        const parsed = JSON.parse(videoUrl);
        videoUrl = parsed.resultUrls?.[0] || parsed.url || videoUrl;
      } catch { }
    } else if (videoUrl.includes(',')) {
      videoUrl = videoUrl.split(',')[0].trim();
    }

    return videoUrl.startsWith('http') ? videoUrl : null;
  };

  // Handle selecting a video from history
  const handleSelectVideoFromHistory = (resultUrl: string) => {
    const videoUrl = getFirstVideoUrlFromResult(resultUrl);
    if (videoUrl) {
      setConfig(prev => ({ ...prev, videoUrl }));
      setShowVideoHistory(false);
      toast({
        title: t("เลือกวิดีโอแล้ว", "Video Selected"),
        description: t("URL วิดีโอถูกเพิ่มแล้ว", "Video URL has been added")
      });
    }
  };

  // Open video history dialog
  const handleOpenVideoHistory = () => {
    setShowVideoHistory(true);
    fetchVideoHistory();
  };

  const saveToHistory = (item: HistoryItem) => {
    const updated = [item, ...historyItems].slice(0, 20);
    setHistoryItems(updated);
    localStorage.setItem(HISTORY_KEY, JSON.stringify(updated));
  };

  const formatTimeRemaining = (expiresAt: number): string => {
    const now = Date.now();
    const remaining = expiresAt - now;
    if (remaining <= 0) return t("หมดอายุแล้ว", "Expired");

    const hours = Math.floor(remaining / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;

    if (days > 0) {
      return `${days} ${t("วัน", "days")} ${remainingHours} ${t("ชม.", "hrs")}`;
    }
    return `${hours} ${t("ชั่วโมง", "hours")}`;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: t("คัดลอกแล้ว", "Copied"),
      description: t("คัดลอก URL ไปยัง Clipboard เรียบร้อย", "URL copied to clipboard")
    });
  };

  // Download video using fetch+blob to force download cross-origin files
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadVideo = async (url: string, filename?: string) => {
    setIsDownloading(true);
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error('Download failed');

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename || `watermark_${Date.now()}.mp4`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(blobUrl);

      toast({
        title: t("ดาวน์โหลดสำเร็จ", "Download Complete"),
        description: t("วิดีโอถูกบันทึกแล้ว", "Video has been saved")
      });
    } catch (error) {
      console.error('Download error:', error);
      // Fallback: open in new tab
      window.open(url, '_blank');
      toast({
        variant: "destructive",
        title: t("ดาวน์โหลดล้มเหลว", "Download Failed"),
        description: t("กรุณาคลิกขวาที่วิดีโอแล้วเลือก 'บันทึกเป็น'", "Please right-click on video and select 'Save as'")
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    if (!file.type.startsWith('image/')) {
      toast({
        variant: "destructive",
        title: t("ไฟล์ไม่ถูกต้อง", "Invalid File"),
        description: t("กรุณาอัพโหลดไฟล์รูปภาพ", "Please upload an image file")
      });
      return;
    }

    setUploadingImage(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `${user.id}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('generation-inputs')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('generation-inputs')
        .getPublicUrl(filePath);

      setConfig(prev => ({ ...prev, imageUrl: publicUrl }));
      toast({
        title: t("อัพโหลดสำเร็จ", "Upload Complete"),
        description: t("รูปภาพพร้อมใช้งานแล้ว", "Image is ready to use")
      });
    } catch (error: any) {
      console.error("Upload error:", error);
      toast({
        variant: "destructive",
        title: t("อัพโหลดล้มเหลว", "Upload Failed"),
        description: error.message
      });
    } finally {
      setUploadingImage(false);
    }
  };

  // Upload video to WM server
  const handleVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate video file type
    const validTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm'];
    if (!validTypes.includes(file.type)) {
      toast({
        variant: "destructive",
        title: t("ไฟล์ไม่ถูกต้อง", "Invalid File"),
        description: t("กรุณาอัพโหลดไฟล์วิดีโอ (MP4, MOV, AVI, WebM)", "Please upload a video file (MP4, MOV, AVI, WebM)")
      });
      return;
    }

    // Check file size (max 500MB)
    const maxSize = 500 * 1024 * 1024;
    if (file.size > maxSize) {
      toast({
        variant: "destructive",
        title: t("ไฟล์ใหญ่เกินไป", "File Too Large"),
        description: t("ขนาดไฟล์สูงสุด 500MB", "Maximum file size is 500MB")
      });
      return;
    }

    setUploadingVideo(true);
    setVideoUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('video', file);
      formData.append('email', user?.email || 'guest@gencnx.com');

      const xhr = new XMLHttpRequest();

      // Track upload progress
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          setVideoUploadProgress(progress);
        }
      });

      // Handle completion
      const uploadPromise = new Promise<{ url: string }>((resolve, reject) => {
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const response = JSON.parse(xhr.responseText);
              if (response.url) {
                resolve(response);
              } else {
                reject(new Error(response.error || 'Upload failed'));
              }
            } catch {
              reject(new Error('Invalid response from server'));
            }
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        };
        xhr.onerror = () => reject(new Error('Network error during upload'));
        xhr.onabort = () => reject(new Error('Upload cancelled'));
      });

      xhr.open('POST', `${WATERMARK_API_URL}/api/upload`);
      xhr.send(formData);

      const result = await uploadPromise;

      setConfig(prev => ({ ...prev, videoUrl: result.url }));
      toast({
        title: t("อัพโหลดสำเร็จ", "Upload Complete"),
        description: t("วิดีโอพร้อมใส่ลายน้ำแล้ว", "Video is ready for watermarking")
      });
    } catch (error: any) {
      console.error("Video upload error:", error);
      toast({
        variant: "destructive",
        title: t("อัพโหลดล้มเหลว", "Upload Failed"),
        description: error.message
      });
    } finally {
      setUploadingVideo(false);
      setVideoUploadProgress(0);
      // Reset input
      if (videoInputRef.current) {
        videoInputRef.current.value = '';
      }
    }
  };

  const handleSubmit = async () => {
    if (!config.videoUrl.trim()) {
      toast({
        variant: "destructive",
        title: t("กรุณากรอก URL", "Please enter URL"),
        description: t("ต้องใส่ลิงก์ไฟล์วิดีโอ", "Video URL is required")
      });
      return;
    }

    if (!config.enableText && !config.enableImage) {
      toast({
        variant: "destructive",
        title: t("กรุณาเลือกลายน้ำ", "Please select watermark"),
        description: t("ต้องเปิดใช้ลายน้ำข้อความหรือรูปภาพอย่างน้อย 1 อย่าง", "Enable at least text or image watermark")
      });
      return;
    }

    if (config.enableText && !config.text.trim()) {
      toast({
        variant: "destructive",
        title: t("กรุณากรอกข้อความ", "Please enter text"),
        description: t("ต้องใส่ข้อความลายน้ำ", "Watermark text is required")
      });
      return;
    }

    if (config.enableImage && !config.imageUrl.trim()) {
      toast({
        variant: "destructive",
        title: t("กรุณาใส่รูปภาพ", "Please add image"),
        description: t("ต้องอัพโหลดหรือใส่ URL รูปภาพ", "Upload or enter image URL")
      });
      return;
    }

    setIsProcessing(true);
    setJobResult({ status: "queued" });

    try {
      // Build request body according to new API spec (v1.3.0)
      const requestBody: any = {
        video_url: config.videoUrl,
        email: user?.email || "guest@gencnx.com",
        fullhd: config.fullhd,
        video_speed: config.videoSpeed
      };

      // Add text watermark options if enabled
      if (config.enableText) {
        requestBody.text = config.text;
        requestBody.text_size = config.textSize;
        requestBody.text_location = config.textLocation;
        requestBody.text_color = config.textColor;
        requestBody.text_opacity = config.textOpacity / 100;
        requestBody.text_margin = config.textMargin;
      }

      // Add image watermark options if enabled
      if (config.enableImage) {
        requestBody.image_url = config.imageUrl;
        requestBody.image_size = config.imageSize;
        requestBody.image_location = config.imageLocation;
        requestBody.image_opacity = config.imageOpacity / 100;
        requestBody.image_margin = config.imageMargin;
      }

      console.log("Submitting watermark job:", requestBody);

      // Use proxy to avoid CORS issues
      const { data: proxyResponse, error: proxyError } = await supabase.functions.invoke('watermark-api-proxy', {
        body: {
          action: 'createJob',
          body: requestBody
        }
      });

      if (proxyError) {
        throw new Error(proxyError.message || 'Proxy request failed');
      }

      const data = proxyResponse;
      console.log("API Response:", data);

      if (data.job_id) {
        setJobResult({
          status: "queued",
          jobId: data.job_id,
          message: data.message || t("งานถูกเพิ่มเข้าคิวแล้ว", "Job queued")
        });

        // Start polling for status
        pollJobStatus(data.job_id);
      } else {
        setJobResult({
          status: "error",
          message: data.error || data.message || t("เกิดข้อผิดพลาด", "An error occurred")
        });
        setIsProcessing(false);
      }
    } catch (error: any) {
      console.error("Watermark API error:", error);
      setJobResult({
        status: "error",
        message: t("ไม่สามารถเชื่อมต่อ API ได้", "Cannot connect to API")
      });
      setIsProcessing(false);
    }
  };

  const pollJobStatus = async (jobId: string) => {
    const maxAttempts = 120; // 10 minutes max (5s intervals)
    let attempts = 0;

    const checkStatus = async () => {
      if (attempts >= maxAttempts) {
        setJobResult({
          status: "error",
          message: t("หมดเวลาประมวลผล กรุณาลองใหม่", "Processing timeout. Please try again.")
        });
        setIsProcessing(false);
        return;
      }

      try {
        // Use proxy to avoid CORS issues
        const { data: proxyResponse, error: proxyError } = await supabase.functions.invoke('watermark-api-proxy', {
          body: {
            action: 'getJobStatus',
            jobId: jobId
          }
        });

        if (proxyError) {
          throw new Error(proxyError.message);
        }

        const data = proxyResponse;
        console.log("Status check:", data);

        if (data.status === "done") {
          const now = Date.now();
          const expiresAt = now + (EXPIRY_HOURS * 60 * 60 * 1000);

          setJobResult({
            status: "done",
            jobId: jobId,
            resultUrl: data.output_url,
            processingTime: data.processing_time_ms
          });
          setIsProcessing(false);

          // Save to localStorage history
          saveToHistory({
            jobId,
            sourceUrl: config.videoUrl,
            text: config.enableText ? config.text : undefined,
            hasImage: config.enableImage,
            resultUrl: data.output_url,
            createdAt: now,
            expiresAt
          });

          // Deduct credits and save to database history
          const prompt = [
            config.enableText ? `Text: ${config.text}` : null,
            config.enableImage ? `Image watermark` : null,
            `Source: ${config.videoUrl}`
          ].filter(Boolean).join(" | ");
          await saveWatermarkToHistory(data.output_url, prompt);

          toast({
            title: t("สำเร็จ!", "Success!"),
            description: t("ใส่ลายน้ำเสร็จเรียบร้อย", "Watermark added successfully")
          });
        } else if (data.status === "failed") {
          setJobResult({
            status: "failed",
            message: data.error_message || t("เกิดข้อผิดพลาดในการประมวลผล", "Processing error")
          });
          setIsProcessing(false);
        } else if (data.status === "expired") {
          setJobResult({
            status: "expired",
            message: t("งานหมดอายุแล้ว", "Job has expired")
          });
          setIsProcessing(false);
        } else {
          // Still processing (queued or processing)
          setJobResult(prev => ({
            ...prev,
            status: data.status || "processing"
          }));
          attempts++;
          setTimeout(checkStatus, 5000); // Check every 5 seconds
        }
      } catch (error) {
        console.error("Status check error:", error);
        attempts++;
        setTimeout(checkStatus, 5000);
      }
    };

    checkStatus();
  };

  const handleCheckStatus = async () => {
    if (!jobResult?.jobId) return;

    setCheckingStatus(true);
    try {
      // Use proxy to avoid CORS issues
      const { data: proxyResponse, error: proxyError } = await supabase.functions.invoke('watermark-api-proxy', {
        body: {
          action: 'getJobStatus',
          jobId: jobResult.jobId
        }
      });

      if (proxyError) {
        throw new Error(proxyError.message);
      }

      const data = proxyResponse;

      if (data.status === "done") {
        const now = Date.now();
        const expiresAt = now + (EXPIRY_HOURS * 60 * 60 * 1000);

        setJobResult({
          status: "done",
          jobId: jobResult.jobId,
          resultUrl: data.output_url,
          processingTime: data.processing_time_ms
        });
        setIsProcessing(false);

        saveToHistory({
          jobId: jobResult.jobId,
          sourceUrl: config.videoUrl,
          text: config.enableText ? config.text : undefined,
          hasImage: config.enableImage,
          resultUrl: data.output_url,
          createdAt: now,
          expiresAt
        });

        // Deduct credits and save to database history
        const prompt = [
          config.enableText ? `Text: ${config.text}` : null,
          config.enableImage ? `Image watermark` : null,
          `Source: ${config.videoUrl}`
        ].filter(Boolean).join(" | ");
        await saveWatermarkToHistory(data.output_url, prompt);

        toast({
          title: t("สำเร็จ!", "Success!"),
          description: t("ใส่ลายน้ำเสร็จเรียบร้อย", "Watermark added successfully")
        });
      } else if (data.status === "failed") {
        setJobResult({
          status: "failed",
          message: data.error_message
        });
        setIsProcessing(false);
      } else {
        toast({
          title: t("กำลังประมวลผล", "Processing"),
          description: `${t("สถานะ", "Status")}: ${data.status}`
        });
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: t("เกิดข้อผิดพลาด", "Error"),
        description: t("ไม่สามารถตรวจสอบสถานะได้", "Cannot check status")
      });
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleReset = () => {
    // Only reset video URL and job state, keep watermark settings
    setConfig(prev => ({ ...prev, videoUrl: "" }));
    setJobResult({ status: "idle" });
    setIsProcessing(false);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      {!user && <GuestOverlay />}

      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-8">
            <Badge variant="secondary" className="mb-4">
              <Stamp className="h-3 w-3 mr-1" />
              {t("สร้างลายน้ำ", "Add Watermark")}
            </Badge>
            <h1 className="text-3xl md:text-4xl font-heading font-bold mb-2">
              <span className="text-gradient">{t("ใส่ลายน้ำ", "Watermark")}</span> {t("บนวิดีโอของคุณ", "Your Videos")}
            </h1>
            <p className="text-muted-foreground">
              {t(
                "เพิ่มลายน้ำข้อความหรือรูปภาพบนวิดีโอ ปกป้องผลงานของคุณ",
                "Add text or image watermark to videos. Protect your content."
              )}
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {/* Config Panel */}
            <div className="lg:col-span-2 space-y-6">
              {/* Channel Profiles */}
              <Card variant="glass">
                <CardHeader>
                  <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Stamp className="h-5 w-5" />
                        {t("โปรไฟล์ช่อง", "Channel Profiles")}
                      </CardTitle>
                      <CardDescription>
                        {t(
                          "จำข้อความ ขนาด ตำแหน่ง รูปภาพ และค่าลายน้ำแยกตามแต่ละช่อง",
                          "Save text, size, position, image, and watermark settings separately for each channel"
                        )}
                      </CardDescription>
                    </div>
                    <Badge variant="secondary" className="w-fit">
                      {channels.length} {t("ช่อง", "channels")}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <select
                      value={activeChannel?.id || ""}
                      onChange={(e) => handleSelectChannel(e.target.value)}
                      disabled={isProcessing}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {channels.map(channel => (
                        <option key={channel.id} value={channel.id}>
                          {channel.name}
                        </option>
                      ))}
                    </select>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={openCreateChannelDialog}
                        disabled={isProcessing}
                        className="flex-1 sm:flex-none"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        {t("สร้าง", "Create")}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={openEditChannelDialog}
                        disabled={isProcessing || !activeChannel}
                        className="flex-1 sm:flex-none"
                      >
                        <Pencil className="h-4 w-4 mr-2" />
                        {t("แก้ไข", "Edit")}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={handleDeleteChannel}
                        disabled={isProcessing || channels.length <= 1}
                        className="flex-1 sm:flex-none"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        {t("ลบ", "Delete")}
                      </Button>
                    </div>
                  </div>
                  <div className="rounded-lg bg-muted/50 p-3 text-sm text-muted-foreground">
                    <span className="font-medium text-foreground">
                      {activeChannel?.name || t("ช่องหลัก", "Default Channel")}
                    </span>
                    {" - "}
                    {config.enableText && config.text
                      ? t(`ข้อความ: ${config.text}`, `Text: ${config.text}`)
                      : t("ยังไม่ได้ตั้งค่าข้อความลายน้ำ", "No text watermark set")}
                  </div>
                </CardContent>
              </Card>

              {/* Video URL */}
              <Card variant="glass">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <LinkIcon className="h-5 w-5" />
                    {t("URL วิดีโอต้นฉบับ", "Source Video URL")}
                  </CardTitle>
                  <CardDescription>
                    {t("วาง URL ของไฟล์วิดีโอ (MP4, MOV)", "Paste URL of your video file (MP4, MOV)")}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <Input
                        placeholder="https://example.com/video.mp4"
                        value={config.videoUrl}
                        onChange={(e) => setConfig({ ...config, videoUrl: e.target.value })}
                        disabled={isProcessing || uploadingVideo}
                        className="flex-1"
                      />
                      <Button
                        variant="outline"
                        onClick={() => videoInputRef.current?.click()}
                        disabled={isProcessing || uploadingVideo}
                        className="shrink-0"
                      >
                        {uploadingVideo ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Upload className="h-4 w-4" />
                        )}
                        <span className="ml-2 hidden sm:inline">{t("อัพโหลด", "Upload")}</span>
                      </Button>
                      <Button
                        variant="outline"
                        onClick={handleOpenVideoHistory}
                        disabled={isProcessing || uploadingVideo || !user}
                        className="shrink-0"
                        title={t("เลือกจากประวัติ", "Select from History")}
                      >
                        <History className="h-4 w-4" />
                        <span className="ml-2 hidden sm:inline">{t("ประวัติ", "History")}</span>
                      </Button>
                      <input
                        ref={videoInputRef}
                        type="file"
                        accept="video/mp4,video/quicktime,video/x-msvideo,video/webm,.mp4,.mov,.avi,.webm"
                        className="hidden"
                        onChange={handleVideoUpload}
                      />
                    </div>
                    {uploadingVideo && (
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Loader2 className="h-3 w-3 animate-spin" />
                          {t("กำลังอัพโหลด", "Uploading")}... {videoUploadProgress}%
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary transition-all duration-300"
                            style={{ width: `${videoUploadProgress}%` }}
                          />
                        </div>
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground">
                      {t("วาง URL หรืออัพโหลดไฟล์วิดีโอ (MP4, MOV, AVI, WebM สูงสุด 500MB)", "Paste URL or upload video file (MP4, MOV, AVI, WebM max 500MB)")}
                    </p>
                  </div>

                  {/* Video Speed */}
                  <div className="space-y-2">
                    <Label>{t("ความเร็ววิดีโอ", "Video Speed")}: {config.videoSpeed.toFixed(2)}x</Label>
                    <Slider
                      value={[config.videoSpeed * 100]}
                      onValueChange={(value) => setConfig({ ...config, videoSpeed: value[0] / 100 })}
                      min={90}
                      max={110}
                      step={1}
                      disabled={isProcessing}
                    />
                    <div className="flex gap-2 flex-wrap">
                      {speedPresets.map((speed) => (
                        <Button
                          key={speed.value}
                          variant={config.videoSpeed === speed.value ? "default" : "outline"}
                          size="sm"
                          onClick={() => setConfig({ ...config, videoSpeed: speed.value })}
                          disabled={isProcessing}
                        >
                          {speed.label}
                        </Button>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {config.videoSpeed < 1
                        ? t(`ช้าลง ${((1 - config.videoSpeed) * 100).toFixed(0)}%`, `${((1 - config.videoSpeed) * 100).toFixed(0)}% slower`)
                        : config.videoSpeed > 1
                          ? t(`เร็วขึ้น ${((config.videoSpeed - 1) * 100).toFixed(0)}%`, `${((config.videoSpeed - 1) * 100).toFixed(0)}% faster`)
                          : t("ความเร็วปกติ", "Normal speed")}
                    </p>
                  </div>

                  {/* Full HD Toggle */}
                  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div className="space-y-0.5">
                      <Label className="text-sm font-medium">{t("ซูมและครอปเป็น Full HD", "Zoom & Crop to Full HD")}</Label>
                      <p className="text-xs text-muted-foreground">
                        {t("ปรับวิดีโอให้เต็มจอ 1920x1080", "Resize video to 1920x1080")}
                      </p>
                    </div>
                    <Switch
                      checked={config.fullhd}
                      onCheckedChange={(checked) => setConfig({ ...config, fullhd: checked })}
                      disabled={isProcessing}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Text Watermark */}
              <Card variant="glass">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <Type className="h-5 w-5" />
                      {t("ลายน้ำข้อความ", "Text Watermark")}
                    </CardTitle>
                    <Switch
                      checked={config.enableText}
                      onCheckedChange={(checked) => setConfig({ ...config, enableText: checked })}
                      disabled={isProcessing}
                    />
                  </div>
                </CardHeader>
                {config.enableText && (
                  <CardContent className="space-y-4">
                    <Input
                      placeholder={t("เช่น @YourChannel", "e.g. @YourChannel")}
                      value={config.text}
                      onChange={(e) => setConfig({ ...config, text: e.target.value })}
                      disabled={isProcessing}
                    />

                    {/* Text Size */}
                    <div className="space-y-2">
                      <Label>{t("ขนาดตัวอักษร", "Font Size")}: {config.textSize}px</Label>
                      <Slider
                        value={[config.textSize]}
                        onValueChange={(value) => setConfig({ ...config, textSize: value[0] })}
                        min={12}
                        max={72}
                        step={2}
                        disabled={isProcessing}
                      />
                    </div>

                    {/* Text Color */}
                    <div className="space-y-2">
                      <Label>{t("สีตัวอักษร", "Font Color")}</Label>
                      <div className="flex gap-2 flex-wrap">
                        {colorOptions.map((color) => (
                          <button
                            key={color.id}
                            onClick={() => setConfig({ ...config, textColor: color.value })}
                            disabled={isProcessing}
                            className={`w-10 h-10 rounded-lg border-2 transition-all ${config.textColor === color.value
                              ? "border-primary scale-110 ring-2 ring-primary/50"
                              : "border-border hover:border-primary/50"
                              }`}
                            style={{ backgroundColor: color.value }}
                            title={t(color.labelTh, color.labelEn)}
                          />
                        ))}
                      </div>
                    </div>

                    {/* Text Location - Visual Grid */}
                    <div className="space-y-2">
                      <Label>{t("ตำแหน่ง", "Position")}</Label>
                      <div className="grid grid-cols-3 gap-1 w-fit bg-muted/50 p-2 rounded-lg border mb-2">
                        {locationGrid.flat().map((pos) => (
                          <button
                            key={pos.id}
                            onClick={() => setConfig({ ...config, textLocation: pos.id })}
                            disabled={isProcessing}
                            className={`w-10 h-10 rounded-md flex items-center justify-center text-lg font-bold transition-all ${config.textLocation === pos.id
                              ? "bg-primary text-primary-foreground shadow-lg scale-110"
                              : "bg-background hover:bg-accent text-muted-foreground hover:text-foreground"
                              }`}
                            title={pos.id}
                          >
                            {pos.labelTh}
                          </button>
                        ))}
                      </div>
                      <Button
                        variant={config.textLocation === "bouncing" ? "default" : "outline"}
                        className="w-full max-w-[136px]"
                        onClick={() => setConfig({ ...config, textLocation: "bouncing" })}
                        disabled={isProcessing}
                      >
                        <Move className="w-4 h-4 mr-2" />
                        {t("เด้งไปมา", "Bouncing")}
                      </Button>
                    </div>

                    {/* Text Opacity */}
                    <div className="space-y-2">
                      <Label>{t("ความโปร่งใส", "Opacity")}: {config.textOpacity}%</Label>
                      <Slider
                        value={[config.textOpacity]}
                        onValueChange={(value) => setConfig({ ...config, textOpacity: value[0] })}
                        min={10}
                        max={100}
                        step={5}
                        disabled={isProcessing}
                      />
                    </div>

                    {/* Text Margin */}
                    <div className="space-y-2">
                      <Label>{t("ระยะห่างจากขอบ", "Margin from edge")}: {config.textMargin}%</Label>
                      <Slider
                        value={[config.textMargin]}
                        onValueChange={(value) => setConfig({ ...config, textMargin: value[0] })}
                        min={2}
                        max={20}
                        step={1}
                        disabled={isProcessing}
                      />
                    </div>
                  </CardContent>
                )}
              </Card>

              {/* Image Watermark */}
              <Card variant="glass">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <ImageIcon className="h-5 w-5" />
                      {t("ลายน้ำรูปภาพ", "Image Watermark")}
                    </CardTitle>
                    <Switch
                      checked={config.enableImage}
                      onCheckedChange={(checked) => setConfig({ ...config, enableImage: checked })}
                      disabled={isProcessing}
                    />
                  </div>
                </CardHeader>
                {config.enableImage && (
                  <CardContent className="space-y-4">
                    {/* Image Upload/URL */}
                    <div className="space-y-2">
                      <Label>{t("รูปภาพโลโก้", "Logo Image")}</Label>
                      <div className="flex gap-2">
                        <Input
                          placeholder="https://example.com/logo.png"
                          value={config.imageUrl}
                          onChange={(e) => setConfig({ ...config, imageUrl: e.target.value })}
                          disabled={isProcessing || uploadingImage}
                          className="flex-1"
                        />
                        <Button
                          variant="outline"
                          onClick={() => imageInputRef.current?.click()}
                          disabled={isProcessing || uploadingImage}
                        >
                          {uploadingImage ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Upload className="h-4 w-4" />
                          )}
                        </Button>
                        <input
                          ref={imageInputRef}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleImageUpload}
                        />
                      </div>
                      {config.imageUrl && (
                        <div className="relative w-20 h-20 bg-muted rounded-lg overflow-hidden">
                          <img
                            src={config.imageUrl}
                            alt="Logo preview"
                            className="w-full h-full object-contain"
                          />
                          <button
                            onClick={() => setConfig({ ...config, imageUrl: "" })}
                            className="absolute top-1 right-1 bg-destructive text-destructive-foreground rounded-full p-0.5"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Image Size */}
                    <div className="space-y-2">
                      <Label>{t("ขนาดรูปภาพ", "Image Size")}: {config.imageSize}px</Label>
                      <Slider
                        value={[config.imageSize]}
                        onValueChange={(value) => setConfig({ ...config, imageSize: value[0] })}
                        min={32}
                        max={256}
                        step={8}
                        disabled={isProcessing}
                      />
                    </div>

                    {/* Image Location - Visual Grid */}
                    <div className="space-y-2">
                      <Label>{t("ตำแหน่ง", "Position")}</Label>
                      <div className="grid grid-cols-3 gap-1 w-fit bg-muted/50 p-2 rounded-lg border mb-2">
                        {locationGrid.flat().map((pos) => (
                          <button
                            key={pos.id}
                            onClick={() => setConfig({ ...config, imageLocation: pos.id })}
                            disabled={isProcessing}
                            className={`w-10 h-10 rounded-md flex items-center justify-center text-lg font-bold transition-all ${config.imageLocation === pos.id
                              ? "bg-primary text-primary-foreground shadow-lg scale-110"
                              : "bg-background hover:bg-accent text-muted-foreground hover:text-foreground"
                              }`}
                            title={pos.id}
                          >
                            {pos.labelTh}
                          </button>
                        ))}
                      </div>
                      <Button
                        variant={config.imageLocation === "bouncing" ? "default" : "outline"}
                        className="w-full max-w-[136px]"
                        onClick={() => setConfig({ ...config, imageLocation: "bouncing" })}
                        disabled={isProcessing}
                      >
                        <Move className="w-4 h-4 mr-2" />
                        {t("เด้งไปมา", "Bouncing")}
                      </Button>
                    </div>

                    {/* Image Opacity */}
                    <div className="space-y-2">
                      <Label>{t("ความโปร่งใส", "Opacity")}: {config.imageOpacity}%</Label>
                      <Slider
                        value={[config.imageOpacity]}
                        onValueChange={(value) => setConfig({ ...config, imageOpacity: value[0] })}
                        min={10}
                        max={100}
                        step={5}
                        disabled={isProcessing}
                      />
                    </div>

                    {/* Image Margin */}
                    <div className="space-y-2">
                      <Label>{t("ระยะห่างจากขอบ", "Margin from edge")}: {config.imageMargin}%</Label>
                      <Slider
                        value={[config.imageMargin]}
                        onValueChange={(value) => setConfig({ ...config, imageMargin: value[0] })}
                        min={2}
                        max={20}
                        step={1}
                        disabled={isProcessing}
                      />
                    </div>
                  </CardContent>
                )}
              </Card>

              {/* Submit Button */}
              <Button
                variant="hero"
                size="lg"
                className="w-full"
                onClick={handleSubmit}
                disabled={isProcessing || !config.videoUrl || (!config.enableText && !config.enableImage) || (config.enableText && !config.text) || (config.enableImage && !config.imageUrl)}
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    {t("กำลังประมวลผล...", "Processing...")}
                  </>
                ) : (
                  <>
                    <Stamp className="h-5 w-5 mr-2" />
                    {t("ใส่ลายน้ำ", "Add Watermark")}
                    <Badge variant="secondary" className="ml-2">
                      {WATERMARK_CREDITS} {t("เครดิต", "credits")}
                    </Badge>
                  </>
                )}
              </Button>

              {/* Tips */}
              <Card variant="glass">
                <CardHeader>
                  <CardTitle className="text-sm">{t("เคล็ดลับ", "Tips")}</CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-muted-foreground space-y-2">
                  <p>• {t("รองรับ MP4, MOV", "Supports MP4, MOV")}</p>
                  <p>• {t("URL ต้องเข้าถึงได้จากอินเทอร์เน็ต", "URL must be publicly accessible")}</p>
                  <p>• {t("สามารถใส่ทั้งข้อความและรูปภาพพร้อมกันได้", "Can add both text and image watermarks")}</p>
                  <p>• {t("ไฟล์หมดอายุใน 72 ชั่วโมง", "Files expire in 72 hours")}</p>
                </CardContent>
              </Card>
            </div>

            {/* Preview & Result Panel */}
            <div className="space-y-4">
              {/* Result Card - Show on top when processing/done */}
              {jobResult.status !== "idle" && (
                <Card variant="glass">
                  <CardHeader>
                    <CardTitle>{t("ผลลัพธ์", "Result")}</CardTitle>
                    <CardDescription>
                      {t("ดาวน์โหลดวิดีโอที่ใส่ลายน้ำแล้ว", "Download your watermarked video")}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Status Display */}
                    <div className="space-y-4">
                      {(jobResult.status === "queued" || jobResult.status === "processing") && (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-amber-500">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            {jobResult.status === "queued" ? t("อยู่ในคิว", "Queued") : t("กำลังประมวลผล", "Processing")}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {t(
                              "ระบบกำลังใส่ลายน้ำ อาจใช้เวลา 1-5 นาที",
                              "Adding watermark. This may take 1-5 minutes."
                            )}
                          </p>
                          {jobResult.jobId && (
                            <p className="text-xs text-muted-foreground font-mono">
                              Job ID: {jobResult.jobId.slice(0, 8)}...
                            </p>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleCheckStatus}
                            disabled={checkingStatus}
                          >
                            {checkingStatus ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <RefreshCw className="h-4 w-4 mr-2" />
                            )}
                            {t("ตรวจสอบสถานะ", "Check Status")}
                          </Button>
                        </div>
                      )}

                      {jobResult.status === "done" && jobResult.resultUrl && (
                        <div className="space-y-4">
                          <div className="flex items-center gap-2 text-green-500">
                            <CheckCircle2 className="h-5 w-5" />
                            {t("เสร็จสิ้น!", "Completed!")}
                          </div>

                          {jobResult.processingTime && (
                            <p className="text-xs text-muted-foreground">
                              {t("เวลาประมวลผล", "Processing time")}: {(jobResult.processingTime / 1000).toFixed(1)}s
                            </p>
                          )}

                          {/* Preview */}
                          <video
                            src={jobResult.resultUrl}
                            controls
                            className="w-full rounded-lg"
                          />

                          <div className="flex gap-2">
                            <Button
                              variant="hero"
                              className="flex-1"
                              onClick={() => handleDownloadVideo(jobResult.resultUrl!)}
                              disabled={isDownloading}
                            >
                              {isDownloading ? (
                                <>
                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                  {t("กำลังดาวน์โหลด...", "Downloading...")}
                                </>
                              ) : (
                                <>
                                  <Download className="h-4 w-4 mr-2" />
                                  {t("ดาวน์โหลด", "Download")}
                                </>
                              )}
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => copyToClipboard(jobResult.resultUrl!)}
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                          </div>

                          <Button variant="outline" className="w-full" onClick={handleReset}>
                            {t("สร้างใหม่", "Create New")}
                          </Button>
                        </div>
                      )}

                      {(jobResult.status === "failed" || jobResult.status === "error" || jobResult.status === "expired") && (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-red-500">
                            <AlertTriangle className="h-5 w-5" />
                            {jobResult.status === "expired" ? t("หมดอายุ", "Expired") : t("เกิดข้อผิดพลาด", "Error")}
                          </div>
                          <p className="text-sm text-muted-foreground">{jobResult.message}</p>
                          <Button variant="outline" className="w-full" onClick={handleReset}>
                            {t("ลองใหม่", "Try Again")}
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Live Preview */}
              <Card variant="glass">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2">
                    <Eye className="h-5 w-5" />
                    {t("ตัวอย่าง", "Preview")}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="relative bg-black rounded-lg overflow-hidden flex items-center justify-center min-h-[300px] max-h-[600px]">
                    {config.videoUrl ? (
                      <div className="relative w-fit h-fit max-w-full max-h-full">
                        <video
                          src={config.videoUrl}
                          className="max-w-full max-h-[500px] w-auto h-auto block"
                          controls
                          muted
                          loop
                          autoPlay
                          playsInline
                        />
                        {/* Text Watermark Overlay */}
                        {config.enableText && config.text && (
                          <div
                            className="absolute pointer-events-none whitespace-nowrap"
                            style={{
                              ...getPositionStyle(config.textLocation, config.textMargin),
                              color: config.textColor,
                              opacity: config.textOpacity / 100,
                              fontSize: `${Math.max(config.textSize / 3, 10)}px`,
                              fontWeight: "bold",
                              textShadow: "1px 1px 2px rgba(0,0,0,0.8)"
                            }}
                          >
                            {config.text}
                          </div>
                        )}
                        {/* Image Watermark Overlay */}
                        {config.enableImage && config.imageUrl && (
                          <img
                            src={config.imageUrl}
                            alt="watermark"
                            className="absolute pointer-events-none"
                            style={{
                              ...getPositionStyle(config.imageLocation, config.imageMargin),
                              width: `${Math.max(config.imageSize / 4, 24)}px`,
                              height: "auto",
                              opacity: config.imageOpacity / 100
                            }}
                          />
                        )}
                      </div>
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground gap-2 min-h-[200px]">
                        <Video className="h-12 w-12 opacity-30" />
                        <p className="text-sm">{t("ใส่ URL วิดีโอเพื่อดูตัวอย่าง", "Enter video URL to preview")}</p>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2 text-center">
                    {t("ตัวอย่างจะแสดงตำแหน่งลายน้ำ (ขนาดจริงอาจต่างกัน)", "Preview shows watermark position (actual size may vary)")}
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* History Section */}
          {historyItems.length > 0 && (
            <div className="mt-12 max-w-6xl mx-auto">
              <Card variant="glass">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <History className="h-5 w-5" />
                    {t("ประวัติการใส่ลายน้ำ", "Watermark History")}
                  </CardTitle>
                  <CardDescription>
                    {t("ไฟล์จะหมดอายุใน 72 ชั่วโมง หลังจากสร้าง", "Files expire 72 hours after creation")}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {historyItems.map((item) => (
                      <div
                        key={item.jobId}
                        className="flex flex-col md:flex-row md:items-center gap-3 p-4 bg-muted/50 rounded-lg"
                      >
                        {/* Preview thumbnail */}
                        <div className="flex-shrink-0 w-full md:w-24 h-16 bg-muted rounded overflow-hidden">
                          <video
                            src={item.resultUrl}
                            className="w-full h-full object-cover"
                            muted
                          />
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">
                            {item.text && <span>{t("ข้อความ", "Text")}: "{item.text}"</span>}
                            {item.text && item.hasImage && " + "}
                            {item.hasImage && <span>{t("รูปภาพ", "Image")}</span>}
                          </p>
                          <p className="text-sm text-muted-foreground truncate">
                            {item.sourceUrl}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <Clock className="h-3 w-3 text-muted-foreground" />
                            <span className="text-xs text-muted-foreground">
                              {t("หมดอายุใน", "Expires in")} {formatTimeRemaining(item.expiresAt)}
                            </span>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2 flex-shrink-0">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => copyToClipboard(item.resultUrl)}
                          >
                            <Copy className="h-4 w-4 mr-1" />
                            {t("คัดลอก", "Copy")}
                          </Button>
                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => handleDownloadVideo(item.resultUrl)}
                          >
                            <Download className="h-4 w-4 mr-1" />
                            {t("ดาวน์โหลด", "Download")}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            asChild
                          >
                            <a
                              href={item.resultUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </main>

      {/* Channel Create/Edit Dialog */}
      <Dialog open={channelDialogOpen} onOpenChange={setChannelDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {channelDialogMode === "create"
                ? t("สร้างโปรไฟล์ช่อง", "Create Channel Profile")
                : t("แก้ไขโปรไฟล์ช่อง", "Edit Channel Profile")}
            </DialogTitle>
            <DialogDescription>
              {t(
                "แต่ละช่องจะจำค่าลายน้ำของตัวเอง เช่น ข้อความ ขนาด ตำแหน่ง และรูปภาพ",
                "Each channel keeps its own watermark settings, including text, size, position, and image"
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t("ชื่อช่อง", "Channel Name")}</Label>
              <Input
                value={channelNameInput}
                onChange={(e) => setChannelNameInput(e.target.value)}
                placeholder={t("เช่น ช่อง A", "e.g. Channel A")}
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label>{t("ข้อความลายน้ำ", "Watermark Text")}</Label>
              <Input
                value={channelTextInput}
                onChange={(e) => setChannelTextInput(e.target.value)}
                placeholder={t("เช่น AAAA chanel", "e.g. AAAA channel")}
              />
              <p className="text-xs text-muted-foreground">
                {t(
                  "หลังสร้างแล้ว ปรับขนาด ตำแหน่ง สี และค่าอื่นๆ ได้จากฟอร์มด้านล่าง ระบบจะจำเฉพาะช่องนี้",
                  "After creating, adjust size, position, color, and other settings below. They will be saved only for this channel"
                )}
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setChannelDialogOpen(false)}>
                {t("ยกเลิก", "Cancel")}
              </Button>
              <Button onClick={handleSaveChannel}>
                {channelDialogMode === "create" ? t("สร้างช่อง", "Create Channel") : t("บันทึก", "Save")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Video History Picker Dialog */}
      <Dialog open={showVideoHistory} onOpenChange={setShowVideoHistory}>
        <DialogContent className="max-w-4xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Video className="h-5 w-5" />
              {t("เลือกวิดีโอจากประวัติ", "Select Video from History")}
            </DialogTitle>
            <DialogDescription>
              {t(
                "เลือกวิดีโอที่คุณสร้างไว้ก่อนหน้านี้เพื่อนำมาใส่ลายน้ำ",
                "Select a video you created previously to add watermark"
              )}
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="h-[60vh] pr-4">
            {loadingVideoHistory ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <span className="ml-2 text-muted-foreground">
                  {t("กำลังโหลด...", "Loading...")}
                </span>
              </div>
            ) : videoHistoryItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Video className="h-12 w-12 text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">
                  {t("ไม่พบวิดีโอในประวัติ", "No videos found in history")}
                </p>
                <p className="text-sm text-muted-foreground/70 mt-1">
                  {t(
                    "สร้างวิดีโอใหม่แล้วกลับมาเลือกได้",
                    "Create new videos and come back to select"
                  )}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {videoHistoryItems.map((item) => {
                  const videoUrl = getFirstVideoUrlFromResult(item.result_url);
                  return (
                    <div
                      key={item.id}
                      className="group relative bg-muted rounded-lg overflow-hidden cursor-pointer border-2 border-transparent hover:border-primary transition-all"
                      onClick={() => handleSelectVideoFromHistory(item.result_url)}
                    >
                      {/* Video Preview */}
                      <div className="aspect-video bg-black/20 relative">
                        {videoUrl && (
                          <video
                            src={videoUrl}
                            className="w-full h-full object-cover"
                            muted
                            preload="metadata"
                            onMouseEnter={(e) => {
                              const video = e.currentTarget;
                              video.currentTime = 0;
                              video.play().catch(() => { });
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.pause();
                              e.currentTarget.currentTime = 0;
                            }}
                          />
                        )}
                        {/* Overlay */}
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button size="sm" variant="secondary">
                              {t("เลือก", "Select")}
                            </Button>
                          </div>
                        </div>
                      </div>

                      {/* Info */}
                      <div className="p-3 space-y-1">
                        <div className="flex items-center justify-between">
                          <Badge variant="secondary" className="text-xs">
                            {item.model}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {new Date(item.created_at).toLocaleDateString('th-TH', {
                              day: 'numeric',
                              month: 'short',
                              year: '2-digit'
                            })}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-2" title={item.prompt}>
                          {item.prompt.length > 50 ? item.prompt.substring(0, 50) + "..." : item.prompt}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Watermark;
